/**
 * Hardware-back dismiss stack for the native shell.
 *
 * CAMP-2026-0449 — "the mobile Back button navigates to the Dashboard instead
 * of the previous screen".
 *
 * Root cause: the Android back button was wired straight to `navigate(-1)`, so
 * it ALWAYS popped a route entry — even when the thing on screen wasn't a route
 * at all. Open Fee Management from the dashboard, tap a student to open the
 * detail sheet, press back: the sheet is a modal (React state, no history
 * entry), so back skipped straight past it and popped `/finance` → `/dashboard`.
 * From the user's seat the "previous screen" was the fee list they were looking
 * at a moment ago, and they got dumped on the Dashboard. Every modal, sheet,
 * drawer and full-screen overlay in the app shares that shape, which is why the
 * tester saw it "regardless of the previous screen".
 *
 * Fix: a LIFO stack of dismiss handlers. Overlays register while they're open
 * (via `useNativeBackDismiss`) and the back button consumes the top handler
 * first; only when the stack is empty does it fall through to history. That is
 * the Android platform contract — back closes what's on top, then leaves the
 * screen — and it makes back symmetric with the overlay's own close button.
 *
 * Deliberately dependency-free (no React, no Capacitor) so the shared modal
 * primitives can import it without pulling the native bridge into the web
 * bundle, and so the ordering logic is unit-testable on its own.
 */

export type BackDismissHandler = () => void;

/**
 * Registered dismiss handlers, oldest first. The LAST entry is the top-most
 * overlay — the one the user sees and expects back to close.
 */
const stack: BackDismissHandler[] = [];

/**
 * Register a dismiss handler. Returns an unregister function; call it when the
 * overlay closes (or unmounts) — leaking an entry would swallow back presses
 * forever.
 *
 * Registering the SAME function twice is treated as one entry so a double
 * effect-run (React StrictMode's dev double-mount) can't require two back
 * presses to close one sheet.
 */
export function pushBackDismissHandler(handler: BackDismissHandler): () => void {
  if (!stack.includes(handler)) stack.push(handler);
  return () => {
    const i = stack.lastIndexOf(handler);
    if (i !== -1) stack.splice(i, 1);
  };
}

/**
 * Pop and run the top-most dismiss handler.
 *
 * Returns `true` when a handler ran (the back press is fully handled — the
 * caller must NOT also touch history), `false` when nothing was open.
 *
 * The entry is removed BEFORE invoking so a handler that throws can't wedge the
 * stack, and so a handler that synchronously re-registers (a wizard stepping
 * back into a previous sub-sheet) lands on top rather than being deduped away.
 */
export function consumeBackDismiss(): boolean {
  const handler = stack.pop();
  if (!handler) return false;
  try {
    handler();
  } catch {
    // A broken overlay must not turn the back button into a dead key.
  }
  return true;
}

/** Number of overlays currently claiming the back button. Test/debug only. */
export function backDismissDepth(): number {
  return stack.length;
}

/** Drop every handler. Used by tests; also a safety valve on hard navigation. */
export function resetBackDismissHandlers(): void {
  stack.length = 0;
}
